import type { Incident } from '../../services/incidentsApi';
import { getRawModeEvidenceQueueCopy } from '../incidents/queueEvidenceLabels';
import {
  customerFacingAreaLabel,
  customerFacingCameraLabel,
  customerFacingSafetyRuleLabel,
  formatTimestamp,
} from './workspaceUtils';

export type RepresentativeSafetyCaseOption = {
  value: string;
  /** Search / autocomplete fallback */
  label: string;
  supportingText: string;
  primaryLine: string;
  secondaryLine: string;
  recordId: string;
  seenTimeLabel: string;
  seenAtMs: number;
  patternLabel: string;
  areaLabel: string;
  cameraLabel: string;
  cameraId: string;
  zoneId: string;
  policyType: string;
  zoneLabel: string;
  policyLabel: string;
  windowStart: string;
  windowEnd: string;
  evidenceSummary: string;
  badges: string[];
  rawIncident: any;
  sourceLabel?: string;
};

export const REPRESENTATIVE_SAFETY_CASE_LABEL = 'Representative evidence';

export const REPRESENTATIVE_CASE_AUTO_HELPER =
  'One evidence sample anchors review and ownership for this report. The full recurring risk pattern may include many grouped observations. Change only if you need a different example.';

export const REPRESENTATIVE_CASE_PICKER_INTRO =
  'Choose one evidence record for evidence detail. The report still summarizes the full recurring risk pattern in the selected window.';

/** @deprecated Use REPRESENTATIVE_CASE_PICKER_INTRO */
export const REPRESENTATIVE_CASE_PICKER_HELPER = REPRESENTATIVE_CASE_PICKER_INTRO;

export const REPRESENTATIVE_CASE_FILTER_LABEL = 'Filter evidence records';

export const REPRESENTATIVE_CASE_FILTER_PLACEHOLDER = 'Type to filter the list below';

export const REPRESENTATIVE_CASE_FILTER_HELPER =
  'Filtering applies to the evidence record list. Open the list to browse matching records.';

export const REPRESENTATIVE_CASE_LIST_LABEL = 'Evidence record list';

export const REPRESENTATIVE_CASE_LIST_PLACEHOLDER = 'Click to open the list and choose an evidence record';

export function buildRepresentativeCaseListHint(args: {
  visibleCount: number;
  totalCount: number;
  truncated: boolean;
}): string {
  const visible = Math.max(0, Number(args.visibleCount) || 0);
  const total = Math.max(0, Number(args.totalCount) || 0);
  if (total === 0) {
    return 'No evidence records match the current filters. Widen the time window or clear camera and area filters above.';
  }
  if (args.truncated && total > visible) {
    return `The list shows the ${visible} most recent evidence records (${total} match your filters). Open the list above to browse them. Narrow the time window or filters above to shorten the list.`;
  }
  if (total === 1) {
    return 'One evidence record matches. Open the list above to confirm your choice.';
  }
  return `Open the list above to choose one of ${total} evidence records in this window.`;
}

export const REPRESENTATIVE_CASE_CANDIDATE_LIMIT = 20;

export const IN_SELECTED_REPORT_WINDOW_LABEL = 'In selected report window';

export function normalizeRepresentativeSourceLabel(sourceLabel?: string | null): string | undefined {
  const raw = String(sourceLabel || '').trim();
  if (!raw) return undefined;
  if (/^available in report window$/i.test(raw)) return IN_SELECTED_REPORT_WINDOW_LABEL;
  return raw;
}

export function parseSeenAtMs(windowStart: string): number {
  const parsed = Date.parse(String(windowStart || '').trim());
  return Number.isFinite(parsed) ? parsed : 0;
}

export function deriveRepresentativeCaseEvidenceSummary(incident: any): string {
  if (!incident || typeof incident !== 'object') return 'Evidence status not recorded yet';
  const copy = getRawModeEvidenceQueueCopy(incident as Incident);
  return copy.sub ? `${copy.main} · ${copy.sub}` : copy.main;
}

export function buildRepresentativeCaseBadgeLabels(args: {
  option: RepresentativeSafetyCaseOption;
  selectedValue: string;
  isLatest: boolean;
}): string[] {
  const badges: string[] = [];
  if (args.option.value && args.option.value === args.selectedValue) {
    badges.push('Representative');
  }
  if (args.isLatest) badges.push('Latest');
  const source = String(args.option.sourceLabel || '').trim();
  if (source === IN_SELECTED_REPORT_WINDOW_LABEL) badges.push('In report window');
  const clip = String(args.option.rawIncident?.clip_status || '').toUpperCase();
  const snap = String(args.option.rawIncident?.snapshot_status || '').toUpperCase();
  if (clip === 'AVAILABLE' || snap === 'AVAILABLE') badges.push('Evidence available');
  if (clip === 'PENDING') badges.push('Clip pending');
  return badges;
}

export function buildRepresentativeCasePrimaryLine(seenTimeLabel: string, cameraLabel: string): string {
  const seen = String(seenTimeLabel || '').trim() || 'Time not recorded';
  const camera = String(cameraLabel || '').trim() || 'Camera not recorded';
  return `${seen} · ${camera}`;
}

export function buildRepresentativeCaseSecondaryLine(patternLabel: string, areaLabel: string): string {
  const pattern = String(patternLabel || '').trim() || 'Safety risk not recorded';
  const area = String(areaLabel || '').trim() || 'Area not recorded';
  return `${pattern} · ${area}`;
}

export function buildRepresentativeCaseHeadline(option: RepresentativeSafetyCaseOption): string {
  return [option.patternLabel, option.cameraLabel].filter(Boolean).join(' · ');
}

export function buildRepresentativeCaseSearchBlob(option: RepresentativeSafetyCaseOption): string {
  return [
    option.primaryLine,
    option.secondaryLine,
    option.recordId,
    option.cameraLabel,
    option.areaLabel,
    option.patternLabel,
    option.seenTimeLabel,
    option.sourceLabel,
    option.evidenceSummary,
    option.badges.join(' '),
  ].filter(Boolean).join(' ');
}

export function sortRepresentativeCaseCandidates(
  options: RepresentativeSafetyCaseOption[],
): RepresentativeSafetyCaseOption[] {
  return [...options].sort((left, right) => (right.seenAtMs || 0) - (left.seenAtMs || 0));
}

export function limitRepresentativeCaseCandidates(
  options: RepresentativeSafetyCaseOption[],
  searchTerm: string,
  limit = REPRESENTATIVE_CASE_CANDIDATE_LIMIT,
): { visible: RepresentativeSafetyCaseOption[]; total: number; truncated: boolean } {
  const term = String(searchTerm || '').trim().toLowerCase();
  let filtered = options;
  if (term) {
    filtered = options.filter((option) => buildRepresentativeCaseSearchBlob(option).toLowerCase().includes(term));
  }
  const sorted = sortRepresentativeCaseCandidates(filtered);
  const total = sorted.length;
  return {
    visible: sorted.slice(0, limit),
    total,
    truncated: total > limit,
  };
}

export function decorateRepresentativeCaseCandidates(
  options: RepresentativeSafetyCaseOption[],
  selectedValue: string,
): RepresentativeSafetyCaseOption[] {
  const sorted = sortRepresentativeCaseCandidates(options);
  const latestValue = sorted[0]?.value || '';
  return sorted.map((option) => ({
    ...option,
    badges: buildRepresentativeCaseBadgeLabels({
      option,
      selectedValue,
      isLatest: option.value === latestValue,
    }),
  }));
}

export function pickDefaultRepresentativeCaseId(
  options: RepresentativeSafetyCaseOption[],
  preferredIds: Array<string | null | undefined>,
): string {
  const preferred = preferredIds.map((value) => String(value || '').trim()).filter(Boolean);
  for (const id of preferred) {
    if (options.some((option) => option.value === id)) return id;
  }
  return sortRepresentativeCaseCandidates(options)[0]?.value || '';
}

export function formatRepresentativeSeenLine(option: RepresentativeSafetyCaseOption): string {
  const seen = option.seenTimeLabel || 'Time not recorded';
  const area = option.areaLabel || 'Area not recorded';
  return `Observed: ${seen} · Area: ${area}`;
}

export function buildRepresentativeCaseFromParts(parts: {
  value: string;
  patternLabel: string;
  areaLabel: string;
  cameraLabel: string;
  windowStart: string;
  windowEnd: string;
  cameraId: string;
  zoneId: string;
  policyType: string;
  policyLabel: string;
  zoneLabel: string;
  rawIncident: any;
  sourceLabel?: string;
}): RepresentativeSafetyCaseOption {
  const patternLabel = customerFacingSafetyRuleLabel(parts.patternLabel);
  const areaLabel = customerFacingAreaLabel(parts.areaLabel) || parts.areaLabel;
  const cameraLabel = customerFacingCameraLabel(parts.cameraLabel) || parts.cameraLabel;
  const seenTimeLabel = formatTimestamp(parts.windowStart) || 'Time not recorded';
  const seenAtMs = parseSeenAtMs(parts.windowStart);
  const primaryLine = buildRepresentativeCasePrimaryLine(seenTimeLabel, cameraLabel);
  const secondaryLine = buildRepresentativeCaseSecondaryLine(patternLabel, areaLabel);
  const recordId = String(parts.value || '').trim();
  const sourceLabel = normalizeRepresentativeSourceLabel(parts.sourceLabel);
  const option: RepresentativeSafetyCaseOption = {
    value: recordId,
    label: primaryLine,
    supportingText: `Record: ${recordId}`,
    primaryLine,
    secondaryLine,
    recordId,
    seenTimeLabel,
    seenAtMs,
    patternLabel,
    areaLabel,
    cameraLabel,
    cameraId: parts.cameraId,
    zoneId: parts.zoneId,
    policyType: parts.policyType,
    policyLabel: parts.policyLabel,
    zoneLabel: parts.zoneLabel,
    windowStart: parts.windowStart,
    windowEnd: parts.windowEnd,
    evidenceSummary: deriveRepresentativeCaseEvidenceSummary(parts.rawIncident),
    badges: [],
    rawIncident: parts.rawIncident,
    sourceLabel,
  };
  return option;
}
